import {
  AUTHORIZE_PAYOUT_URL,
  CANCEL_PAYOUT_URL,
  INITIATE_PAYOUT_ASYNC,
  INITIATE_SINGLE_PAYOUT,
  PAYOUTS_AUTHORIZATION_RULE,
  PAYOUTS_BULK,
  PAYOUTS_CONNECT_URL,
  PAYOUTS_SIGN_UP_URL,
  PAYOUTS_SINGLE,
  VALIDATE_BULK_PAYOUT,
} from 'view/payoutsPage/constants';
import { apiFetch } from 'modules/shared/fetch';
import { createCorrelationId } from 'utils/createUniqueId';
import Message from 'view/shared/message';
import {
  GetRefundsParams,
  UpdateRefundRequestParams,
  PaymentDetailsRequestParams,
  ValidateBulkPayoutRequestParams,
  InitiateRefundRequestParams,
  GetAuthorizablesRequestParams,
  GetPaymentDetailsForRefundsParams,
  SchemaResponse,
  InitiatePayoutRequestParams,
  CancelPayoutRequestParams,
  AuthorizePayoutRequestParams,
} from './payout.types';

const correlationId = createCorrelationId();

export default class PayoutService {
  static async getPayoutDetailsForCurrentUser({
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch('/payouts/users/current/payout-details', {
      // @ts-ignore
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async getPayoutDetailsForUsers({
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch('/payouts/users/payout-details', {
      // @ts-ignore
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async optInCurrentUserForPayouts({
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch(PAYOUTS_SIGN_UP_URL, {
      // @ts-ignore
      method: 'POST',
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async autoConnectUserForPayouts({
    body,
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch(PAYOUTS_CONNECT_URL, {
      // @ts-ignore
      method: 'POST',
      body,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async getAuthorizables({
    body,
    appToken,
    appEnvironment,
    mechanism,
  }: GetAuthorizablesRequestParams) {
    const searchParams = new URLSearchParams();

    Object.entries(body).forEach(([key, value]) => {
      searchParams.append(key, value);
    });

    let url = '';

    if (mechanism === PAYOUTS_SINGLE) {
      url = `/payouts/single-payments?${searchParams.toString()}`;
    }

    if (mechanism === PAYOUTS_BULK) {
      url = `/payouts/bulk-payments?${searchParams.toString()}`;
    }

    const response = await apiFetch(url, {
      // @ts-ignore
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async initiatePayout({
    body,
    async,
    appToken,
    appEnvironment,
  }: InitiatePayoutRequestParams) {
    const url = async ? INITIATE_PAYOUT_ASYNC : INITIATE_SINGLE_PAYOUT;
    const response = await apiFetch(url, {
      // @ts-ignore
      method: 'POST',
      body,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });
    if (async) {
      Message.success('Payout(s) submitted');
    } else if (
      response?.data.payouts_payment_details[0].destination_details
        .payment_status === 'CREATED'
    ) {
      Message.success('Payout created');
    } else {
      Message.error(
        response?.data.payouts_payment_details[0].destination_details
          .error_message,
      );
    }
    return response?.data;
  }

  static async cancelPayout({
    body,
    appToken,
    appEnvironment,
  }: CancelPayoutRequestParams) {
    const response = await apiFetch(CANCEL_PAYOUT_URL, {
      // @ts-ignore
      method: 'POST',
      body,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    Message.success('Payout cancelled successfully');
    return response?.data;
  }

  static async authorizePayout({
    body,
    appToken,
    appEnvironment,
  }: AuthorizePayoutRequestParams) {
    await apiFetch(AUTHORIZE_PAYOUT_URL, {
      // @ts-ignore
      method: 'POST',
      body,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    Message.success('Payout(s) submitted for authorization');
  }

  static async validateBulkPayout({
    body,
    accountId,
    appToken,
    appEnvironment,
  }: ValidateBulkPayoutRequestParams) {
    const formData = new FormData();
    // @ts-ignore
    formData.set('file', body);
    formData.set('account_id', accountId);

    const response = await apiFetch(VALIDATE_BULK_PAYOUT, {
      // @ts-ignore
      method: 'POST',
      body: formData,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
      stringifyBody: false,
      removeContentType: true,
    });

    return response?.data;
  }

  static async getPayoutDestinations({
    body,
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const searchParams = new URLSearchParams();

    // @ts-ignore
    Object.entries(body).forEach(([key, value]) => {
      if (typeof value === 'string') {
        searchParams.append(key, value);
      }
    });
    const response = await apiFetch(
      `/payouts/destinations?${searchParams.toString()}`,
      {
        // @ts-ignore
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
          'lean-correlation-id': correlationId,
        },
      },
    );

    return response?.data;
  }

  static async createPayoutAuthorizationRule({
    body,
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch(PAYOUTS_AUTHORIZATION_RULE, {
      // @ts-ignore
      method: 'POST',
      body,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async updatePayoutAuthorizationRule({
    ruleId,
    payload,
    appToken,
    appEnvironment,
  }: any) {
    const response = await apiFetch(`${PAYOUTS_AUTHORIZATION_RULE}/${ruleId}`, {
      // @ts-ignore
      method: 'PUT',
      body: payload,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async getPayoutAuthorizationRule({
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch(PAYOUTS_AUTHORIZATION_RULE, {
      // @ts-ignore
      method: 'GET',
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async getRefunds({
    page,
    status,
    pageSize,
    appToken,
    appEnvironment,
  }: GetRefundsParams) {
    const response = await apiFetch(
      `/payouts/refunds?status=${status}&page=${page}&size=${pageSize}`,
      {
        // @ts-ignore
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
          'lean-correlation-id': correlationId,
        },
      },
    );

    return response?.data;
  }

  static async getRefundReasons({
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch('/payouts/refunds/reasons', {
      // @ts-ignore
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async getPaymentRefundDetailsById({
    appToken,
    paymentId,
    appEnvironment,
  }: GetPaymentDetailsForRefundsParams) {
    const response = await apiFetch(`/payouts/refunds/payments/${paymentId}`, {
      // @ts-ignore
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async postInitiateRefund({
    body,
    appToken,
    appEnvironment,
  }: InitiateRefundRequestParams) {
    const response = await apiFetch('/payouts/refunds', {
      // @ts-ignore
      method: 'POST',
      body,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async updateRefund({
    body,
    appToken,
    appEnvironment,
  }: UpdateRefundRequestParams) {
    const response = await apiFetch('/payouts/refunds', {
      // @ts-ignore
      method: 'PUT',
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
      body,
    });

    return response?.data;
  }

  static async fetchSecretSchema({
    bankId,
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams & {
    bankId: string;
  }): Promise<SchemaResponse> {
    const response = await apiFetch(`/v1/secrets/schema/${bankId}`, {
      // @ts-ignore
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
    });

    return response?.data;
  }

  static async uploadSecrets({
    body,
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch('/upload-secrets-and-authenticate', {
      // @ts-ignore
      method: 'POST',
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
      body,
    });

    return response?.data;
  }

  static async connectToBankUsingSecrets({
    body,
    appToken,
    appEnvironment,
  }: PaymentDetailsRequestParams) {
    const response = await apiFetch('/payouts/connect', {
      // @ts-ignore
      method: 'POST',
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        'lean-correlation-id': correlationId,
      },
      body,
    });

    return response?.data;
  }
}
